import { z } from 'zod';
import type { InternalRole } from '../identity/schema';

/**
 * Audience-safe classification of an internal-user conversation participant.
 *
 * Wire surfaces never carry the org staff role vocabulary — ADR-087 keeps it a
 * display/provisioning concern with its own governance, and ADR-098 Rule 15
 * keeps participant projections audience-relative — so participant projections
 * expose this closed two-value class instead: clinical roles read as the
 * member's care team, org-side roles as support. The class is derived
 * server-side only, and it is never an authorization input.
 */
export const PARTICIPANT_ROLE_CLASS = ['care_team', 'support'] as const;
export type ParticipantRoleClass = (typeof PARTICIPANT_ROLE_CLASS)[number];
export const participantRoleClassSchema = z.enum(PARTICIPANT_ROLE_CLASS);

// A Record over the full org vocabulary so adding a role without classing it
// is a compile error rather than a silent null at runtime.
const ROLE_CLASS_BY_INTERNAL_ROLE: Record<InternalRole, ParticipantRoleClass> = {
  physician: 'care_team',
  prescriber: 'care_team',
  care_specialist: 'care_team',
  counselor: 'care_team',
  coordinator: 'care_team',
  operations: 'support',
  manager: 'support',
  admin: 'support',
  business_operations: 'support',
};

// A Map lookup, not object indexing: a stored value like `constructor` or
// `__proto__` must read as outside the vocabulary, never resolve through
// Object.prototype into a truthy non-class.
const ROLE_CLASS_LOOKUP: ReadonlyMap<string, ParticipantRoleClass> = new Map(
  Object.entries(ROLE_CLASS_BY_INTERNAL_ROLE),
);

/**
 * The class for a stored role value, or null for a value outside the governed
 * vocabulary — callers surface the gap instead of guessing a class.
 */
export function participantRoleClassFor(role: string | null): ParticipantRoleClass | null {
  if (!role) return null;
  return ROLE_CLASS_LOOKUP.get(role) ?? null;
}
